import { companyagreementApi, proposalsApi } from 'app/src/services/welfareportal'
import { Image, PageWidget, PortalConfirmDialog } from 'app/src/shared/components'
import { useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import errorBlock from 'app/static/images/error_block.png'
import successImage from 'app/static/images/success_img.png'
import { useFormik } from 'formik'
import { AutoComplete } from 'primereact/autocomplete'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { InputText } from 'primereact/inputtext'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

interface CategoriesSectionProps {
  onFinalStepCompleted: () => void
}

export function CategoriesSection({ onFinalStepCompleted }: CategoriesSectionProps) {
  const navigate = useWelfarePortalNavigate()
  const context = useWebAppContext()
  const [showModalConfirm, setShowModalConfirm] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [pageContent, setPageContent] = useState<'form' | 'success' | 'error'>('form')
  const [items, setItems] = useState<string[]>([])
  const { t } = useTranslation('proposal', { keyPrefix: 'Proposal' })

  const categoryOptionsMutation = proposalsApi.useProposalsCategoriesList({
    queryParams: {
      pagingOffset: 0,
      pagingLimit: 1,
    },
  })

  const categoryOptions = categoryOptionsMutation.data?.categories ?? []
  const createProposalMutation = companyagreementApi.useProposalCreate()

  const search = (event: { query: string }) => {
    if (!categoryOptions) return // Se è undefined, esce subito dalla funzione
    const query = event.query.toLowerCase()
    const filteredOptions =
      categoryOptions.filter((opt) => opt.productCategory.toLowerCase().includes(query)).map((opt) => opt.productCategory) || []
    setItems(filteredOptions)
  }
  const submitCategoryProposal = useCallback(
    async (values: { companyName: string; selectedCategory: string }) => {
      setLoading(true)
      try {
        const mutateResp = await createProposalMutation.mutateAsync({
          body: {
            companyName: values.companyName,
            productCategory: values.selectedCategory,
          },
        })

        onFinalStepCompleted()
        setShowModalConfirm(false)
        setPageContent('success')
        return mutateResp
      } catch (err) {
        context.logger?.error('Error creating the proposal:', { err })
        setPageContent('error')
        onFinalStepCompleted()
        throw err
      } finally {
        setLoading(false)
      }
    },
    [createProposalMutation, onFinalStepCompleted, context],
  )

  const formik = useFormik({
    initialValues: {
      selectedCategory: '',
      companyName: '',
    },
    validationSchema: Yup.object({
      selectedCategory: Yup.string().required(t('CategoriesSection.ValidationErrorCategory')),
      companyName: Yup.string().required(t('CategoriesSection.ValidationErrorCompanyName')),
    }),
    onSubmit: async (values) => {
      try {
        await submitCategoryProposal(values)
      } catch (err) {
        context.logger?.error('Error Send Suggest proposals', { err })
      }
    },
  })

  const isSubmitDisabled = !formik.values.selectedCategory || !formik.values.companyName

  if (pageContent === 'success') {
    return renderFinalStepSubmitted()
  } else if (pageContent === 'error') {
    return renderErrorGeneric()
  }

  return (
    <PageWidget>
      <div className='grid grid-cols-1'>
        <form onSubmit={formik.handleSubmit} className='w-full'>
          <Card className='bg-[--card-background]'>
            <div dangerouslySetInnerHTML={{ __html: t('CategoriesSection.SuggestCardLabel') }} />
            <div className='my-4'>
              <label htmlFor='selectedCategory' className='mb-2'>
                {t('CategoriesSection.SearchLabel')}
              </label>
              <AutoComplete
                id='selectedCategory'
                value={formik.values.selectedCategory}
                suggestions={items}
                completeMethod={search}
                onChange={(e) => formik.setFieldValue('selectedCategory', e.value)}
                placeholder={t('CategoriesSection.SearchLabelSelected')}
                className='h-12 w-full'
                dropdown
                maxLength={100}
              />
            </div>
            <div className='my-4'>
              <label htmlFor='companyName' className='mb-2'>
                {t('CategoriesSection.InputLabel')}
              </label>
              <InputText
                id='companyName'
                type='text'
                name='companyName'
                placeholder={t('CategoriesSection.InputPlaceholder')}
                className='h-12 w-full border px-3'
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                maxLength={100}
              />
            </div>
            {showModalConfirm && (
              <PortalConfirmDialog
                className='w-[600px]'
                accept={formik.submitForm}
                reject={() => setShowModalConfirm(false)}
                yesText={t('CategoriesSection.ModalConfirmBtnConfirmLabel')}
                cancelText={t('CategoriesSection.ModalConfirmBtnCancelLabel')}
                title={t('CategoriesSection.ModalConfirmTitle')}
                body={null}
              />
            )}
          </Card>
        </form>
      </div>
      {loading && (
        <div className='fixed inset-0 flex items-center justify-center bg-white bg-opacity-75'>
          <ProgressSpinner data-testid='loading-element' />
        </div>
      )}
      <div className='my-5 flex items-center justify-center'>
        <Button
          rounded
          type='button'
          label={t('CategoriesSection.ButtonLabel')}
          className='h-12 px-6 text-base'
          onClick={() => setShowModalConfirm(true)}
          disabled={isSubmitDisabled || loading}
        />
      </div>
    </PageWidget>
  )

  function renderFinalStepSubmitted() {
    return (
      <PageWidget>
        <div className='space-y-6 text-center'>
          <Image
            pt={{ image: { className: 'object-fit mx-auto h-[150px] lg:h-[250px]' } }}
            src={successImage}
            alt="Immagine di successo dell'operazione"
          />
          <div className='text-2xl font-bold'>{t('ProposalsFinalStepSubmitted.Title')}</div>
          <div className='text-lg'>{t('ProposalsFinalStepSubmitted.Subtitle')}</div>
          <div className='text-base'>{t('ProposalsFinalStepSubmitted.Description')}</div>
        </div>
        <div className='mx-auto flex flex-col items-center justify-center'>
          <Button
            rounded
            label={t('ProposalsErrorGeneric.BtnGoToCatalogLabel')}
            className='my-5 h-[38px] min-w-[150px] text-[14px] font-normal'
            onClick={navigate.goToCatalog}
          />
        </div>
      </PageWidget>
    )
  }

  function renderErrorGeneric() {
    return (
      <PageWidget>
        <div className='mb-2 flex flex-col'>
          <div className='mx-auto'>
            <div>
              <Image
                pt={{ image: { className: 'max-h-[257px]' } }}
                className='object-fit aspect-[3/2] max-h-[257px]'
                src={errorBlock}
                alt="Immagine di errore dell'operazione"
              />
            </div>
          </div>
          <div className='bold m-auto mx-auto mt-[24px] max-w-xl text-center text-[22px] font-bold leading-[40px]'>
            {t('ProposalsErrorGeneric.Title')}
          </div>
          <div className='mx-auto flex flex-col items-center justify-center'>
            <Button
              rounded
              label={t('ProposalsErrorGeneric.BtnGoToCatalogLabel')}
              className='my-5 h-[38px] min-w-[150px] text-[14px] font-normal'
              onClick={navigate.goToCatalog}
            />
          </div>
        </div>
      </PageWidget>
    )
  }
}
